import {RmCalendarEntryType, RmDayPart, RmDecisionMode, RmDecisionStatus} from '../../../enums/calendar-enums';
import {
    SearchModel,
    yascDisplayName,
    yascEnum,
    yascFilterByFn,
    yascFilterConfig
} from '@yasc/yasc-material-components';
import {BaseTaskRes, ProjectMinRes} from '../crm';
import {Type} from 'class-transformer';
import {Salutation} from '../../../enums';
import {ApiDateAdapter} from '../../../utils/api-date-adapter';

export class CalendarEntryBaseRes {
    id!: number;
    startUtc!: any;
    endUtc!: any;
    isAllDay!: boolean;
    dayPart!: RmDayPart;
    groupId!: string;
    ownerUserId!: number;
    createdByUserId!: string;
    createdAtUtc?: any;
    updatedAtUtc?: any;
    absenceCreditHours!: number;
    publicHolidayCreditHours?: number;
    absenceDays!: number;
    source?: string;

    constructor(value?: Partial<CalendarEntryBaseRes>) {
        this.startUtc = undefined;
        this.endUtc = undefined;
        this.isAllDay = false;
        this.dayPart = RmDayPart.None as any;
    }
}

export class CalendarEntryRes extends CalendarEntryBaseRes {
    ownerName?: string;
    type!:RmCalendarEntryType

    title?: string;
    description?: string;
    color!: string;

    isActive!: boolean;

    decisionMode!: RmDecisionMode;

    @yascDisplayName('resourceManager_vacationApproved_decisionStatus_label')
    @yascEnum(RmDecisionStatus, 'resourceManager_vacationApproved_decisionStatusEnum')
    @yascFilterByFn((entry: CalendarEntryRes) => entry.decisionStatus)
    @yascFilterConfig({
        getGroup: value => Array.isArray(value) ? value : [value],
        multiselect: true
    })
    decisionStatus!: RmDecisionStatus;
    decisionComment?: string;

    taskId?: string;

    @Type(() => BaseTaskRes)
    task?: BaseTaskRes;

    projectId?: number;
    projectBudgetId?: number;
    consumptionFactor?: number;

    @Type(() => ProjectMinRes)
    project?: ProjectMinRes;

    @Type(() => CalendarDetailsUserRes)
    decidedByUser?: CalendarDetailsUserRes;
    decidedAtUtc?: any;

    creditedHours: number = 0;

    constructor(init?: Partial<CalendarEntryRes>) {
        // reasonable defaults (match backend defaults where possible)
        super(init)
        this.isActive = true;
        this.decisionMode = (RmDecisionMode as any).None ?? (0 as any);
        this.decisionStatus = (RmDecisionStatus as any).None ?? (0 as any);
        Object.assign(this, init);
    }

    get entryDurationInDays() {
        let duration = 0;
        const start = ApiDateAdapter.calendarRangeStart(this);
        const end = ApiDateAdapter.calendarRangeEnd(this, start);
        const current = new Date(start);

        while (current <= end) {
            if (current.getDay() !== 0 && current.getDay() !== 6) {
                duration++;
            }
            current.setDate(current.getDate() + 1);
        }

        return duration;
    }
}

export class CalendarEntryCreateReq {
    start!: any;
    end!: any;

    isAllDay!: boolean;
    dayPart!: RmDayPart;

    carryOver!: boolean;
    overrideDurationMinutes?: number;


    title?: string;
    description?: string;
    color?: string;

    taskId?: string;
    projectId?: number;
    projectBudgetId?: number;
    consumptionFactor?: number;

    rRule?: string;
    recurrenceTimeZoneId?: string;

    constructor(init?: Partial<CalendarEntryCreateReq>) {
        this.isAllDay = false;
        this.dayPart = RmDayPart.None as any;
        this.carryOver = false;

        Object.assign(this, init);
    }
}


export class CalendarEntryUReq {
    id!: number;

    start?: any;
    end?: any;
    isAllDay?: boolean;
    dayPart?: RmDayPart;

    carryOver?: boolean;
    overrideDurationMinutes?: number;


    title?: string;
    description?: string;
    color?: string;

    taskId?: string;
    projectId?: number;
    projectBudgetId?: number;
    consumptionFactor?: number;
    clearProjectBudget?: boolean;
    clearConsumptionFactor?: boolean;

    rRule?: string;
    recurrenceTimeZoneId?: string;

    constructor(init?: Partial<CalendarEntryUReq>) {
        Object.assign(this, init);
    }
}

export class CalendarEntryCreateTypedReq extends CalendarEntryCreateReq {
    type!: RmCalendarEntryType;
    ownerUserId?: number;
    groupId?: string;
    participantUserIds?: number[];

    constructor(init?: Partial<CalendarEntryCreateTypedReq>) {
        super(init);
        Object.assign(this, init);
    }
}

export class CalendarEntryQueryReq {
    userIds?:number[]
    fromUtc?: string;
    toUtc?: string;
    types?: RmCalendarEntryType[];
    decisionStatuses?: RmDecisionStatus[];
    projectIds?: number[];
    onlyActive?: boolean;
    /**
     * When false the backend skips the Project/Task joins and omits their detail objects
     * (ProjectId/TaskId are still returned). The absence overviews opt out; the week timetable
     * keeps the default (true) because it renders project/customer/task.
     */
    includeProjectDetails?: boolean;
    pageIndex?: number;
    pageSize?: number;

    constructor(init?: Partial<CalendarEntryQueryReq>) {
        Object.assign(this, init);
        // ensure defaults even if init provides null/undefined
        this.onlyActive = init?.onlyActive ?? true;
    }

}

export class CalendarEmploymentPeriodRes {
    userId!: number;
    validFromUtc!: any;
    validToUtc?: any;

    constructor(init?: Partial<CalendarEmploymentPeriodRes>) {
        Object.assign(this, init);
    }
}

export class CalendarEmploymentPeriodsQuery {
    userIds: number[] = [];
    fromUtc!: string;
    toUtc!: string;

    constructor(init?: Partial<CalendarEmploymentPeriodsQuery>) {
        Object.assign(this, init);
        this.userIds = [...(init?.userIds ?? [])];
    }
}

export class CalendarMissingEntrySearchReq {
    userIds?: number[]
    excludeWeekends?: boolean;
    excludePublicHoliday?: boolean;
    excludeFutureDays?: boolean;
    fromUtc?: string;
    toUtc?: string;

    constructor(init?: Partial<CalendarEntryQueryReq>) {
        Object.assign(this, init);
    }
}

export class CalendarEntrySearchReq {
    ownerUserId?: number;
    fromUtc?: string;
    toUtc?: string;

    types?: RmCalendarEntryType[];
    decisionStatuses?: RmDecisionStatus[];

    onlyActive: boolean = true;
    search: SearchModel = new SearchModel({});

    constructor(init?: Partial<CalendarEntrySearchReq>) {
        Object.assign(this, init);
        // ensure defaults even if init provides null/undefined
        this.onlyActive = init?.onlyActive ?? true;
        this.search = init?.search ?? new SearchModel({});
    }
}

export class CalendarEntryCancelReq {
    applyToGroup: boolean = true;
    comment?: string;

    constructor(init?: Partial<CalendarEntryCancelReq>) {
        Object.assign(this, init);
        this.applyToGroup = init?.applyToGroup ?? true;
    }
}

export class CalendarInvitationRespondReq {
    entryId!: number;
    status!: RmDecisionStatus;
    comment?: string;

    constructor(init?: Partial<CalendarInvitationRespondReq>) {
        Object.assign(this, init);
    }
}


export class CalendarEntryCreateBatchReq {
    items: CalendarEntryCreateTypedReq[] = [];

    constructor(init?: Partial<CalendarEntryCreateBatchReq>) {
        Object.assign(this, init);
        this.items = init?.items ?? [];
    }
}

export class CalendarDetailsUserRes {
    id!: number;
    title?: string;
    userName!: string;
    lastName!: string;
    givenName!: string;
    email!: string;
    salutationStatus!: Salutation;

    constructor(init?: Partial<CalendarDetailsUserRes>) {
        Object.assign(this, init);
    }

    get fullName() {
        return (this.title !== undefined ? this.title + ' ' : '') + this.givenName + ' ' + this.lastName;
    }
}

export class CalendarMissingEntriesAggregationRes {
    userCount?: number;
    totalExpectedDayCount?: number;
    totalDaysWithEntriesCount?: number;
    totalMissingDaysCount?: number;
}

export class CalendarMissingEntriesUserRes {
    @Type(() => CalendarMissingEntriesUserRes)
    user!: CalendarDetailsUserRes;
    expectedDaysCount?: number;
    daysWithEntriesCount?: number;
    missingDaysCount?: number;
    missingDays: string[] = [];

    get missingDates(){
        return this.missingDays.map(x => new Date(x))
    }
}

export class CalendarMissingEntriesGroupingRes{

}
export class TimeTrackingClosedYearsRes {
    years: number[] = [];
}
